import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
  Input,
  Switch,
  Checkbox,
  Label,
  SpinnerLoader,
  SingleSelect,
} from "lift-ui";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { Toastify } from 'lift-ui';
import { CDType } from "../taxonomy-name.component";
import apiClient from "@lift-admin/utils/apiClient";
import { actionLog } from "@/App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  formatIdWithName,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface CreateDialogProps {
  openCreateDialog: boolean;
  setOpenCreateDialog: (open: boolean) => void;
  mainTab: string;
  taxonomyTypes: any[];
  dialogMode: string;
  setDialogMode: (mode: string) => void;
  editValue: any;
  setEditValue: (value: any) => void;
  setRefresh: any;
  brands: any[];
  dialogValue: any[];
  createModeValue: any;
}

interface AddSDTypeRequest {
  name: string;
  taxonomy_type: string;
  description?: string;
  created_by?: number;
  updated_by?: number;
  is_active: boolean;
  metadata_: any;
}

interface AddCDTypeRequest {
  name: string;
  taxonomy_type: string;
  brand_id: number;
  description?: string;
  created_by?: number;
  updated_by?: number;
  is_active: boolean;
  is_required: boolean;
  metadata_: any;
}

interface AddSDValueRequest {
  display_name: string;
  value: string;
  domain_type_id: number;
  created_by?: number;
  updated_by?: number;
  is_active: boolean;
}

interface CustomSDValueRequest
  extends Omit<AddSDValueRequest, "domain_type_id"> {
  custom_domain_type_id: number;
}

const CreateDialog: React.FC<CreateDialogProps> = ({
  openCreateDialog,
  setOpenCreateDialog,
  mainTab,
  taxonomyTypes,
  dialogMode,
  setDialogMode,
  editValue,
  setEditValue,
  setRefresh,
  brands,
  dialogValue,
  createModeValue,
}) => {
  const [loader, setLoader] = useState(false);
  const [bqColumnName, setBQColumnName] = useState("");
  const [columnName, setColumnName] = useState("");
  const [columnValue, setColumnValue] = useState("");
  const [taxonomyType, setTaxonomyType] = useState("");
  const [brandType, setBrandType] = useState("");
  const [description, setDescription] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [groupedTypes, setGroupedTypes] = useState<any>({});
  const [activeTab, setActiveTab] = useState<string>("");
  const [columnNameError, setColumnNameError] = useState<string | null>(null);
  const [bqColumnNameError, setBQColumnNameError] = useState<string | null>(null);

  let payload:
    | AddSDTypeRequest
    | AddSDValueRequest
    | AddCDTypeRequest
    | CustomSDValueRequest;
  let path: string;

  // Add helper function to validate column name format
  const isValidColumnName = (name: string): boolean => {
    // Only allow alphanumeric characters and spaces
    const validPattern = /^[a-zA-Z0-9 ]+$/;
    return validPattern.test(name);
  };

  const validateColumnName = (
    name: string,
    setter: (error: string | null) => void
  ): boolean => {
    if (!name.trim()) {
      setter("Name is required.");
      return false;
    }

    if (!isValidColumnName(name)) {
      setter(
        "Name cannot contain hyphens, underscores, or special characters."
      );
      return false;
    }

    setter(null);
    return true;
  };

  const validateInputs = (): boolean => {
    let isColumnNameValid = true;
    if (activeTab === "Type") {
      isColumnNameValid = validateColumnName(columnName, setColumnNameError);
    } else {
      if (!columnName.trim()) {
        setColumnNameError("Display name is required.");
        isColumnNameValid = false;
      } else {
        setColumnNameError(null);
      }
    }
    let isBQColumnNameValid = true;
    if (activeTab === "Type" && bqColumnName) {
      isBQColumnNameValid = validateColumnName(
        bqColumnName,
        setBQColumnNameError
      );
    } else {
      setBQColumnNameError(null);
    }
    if (!isColumnNameValid || !isBQColumnNameValid) {
      return false;
    }

    if (!taxonomyType) {
      setValidationError("Type is required.");
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleCreate = async () => {
    if (!validateInputs()) return;

    setLoader(true);
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    let brandName = "";
    if (mainTab === "Custom" && activeTab === "Type" && brandType) {
      const selectedBrand = brands.find((b: any) => b.id === Number(brandType));
      brandName = selectedBrand?.name || brandType;
    }

    // Resolve parent type (and brand) for Value logging
    let taxonomyTypeName = taxonomyType;
    let valueBrandId: number | string | null = null;
    let valueBrandName = "";
    if (activeTab === "Value" && dialogValue) {
      const selectedType = dialogValue.find(
        (t: any) => t.id === Number(taxonomyType)
      );
      taxonomyTypeName = selectedType?.name || taxonomyType;
      valueBrandId = selectedType?.brand_id ?? null;
      if (valueBrandId != null) {
        valueBrandName =
          brands.find((b: any) => b.id === Number(valueBrandId))?.name || "";
      }
    }

    if (mainTab == "Standard") {
      if (activeTab == "Type") {
        payload = {
          name: columnName.trim(),
          taxonomy_type: taxonomyType,
          description: description.trim() || undefined,
          ...(dialogMode === "add"
            ? { created_by: user.id }
            : { updated_by: user.id }),
          is_active: isActive,
          ...(bqColumnName
            ? {
              metadata_: {
                "BQ Column Name": bqColumnName,
              },
            }
            : { metadata_: {} }),
        };
        path =
          dialogMode == "add" ? `sdtype/add` : `sdtype/update/${editValue.id}`;
      } else {
        payload = {
          display_name: columnName.trim(),
          domain_type_id: Number(taxonomyType),
          value: columnValue.trim(),
          ...(dialogMode === "add"
            ? { created_by: user.id }
            : { updated_by: user.id }),
          is_active: isActive,
        };
        path =
          dialogMode == "add"
            ? `sdvalue/add`
            : `sdvalue/update/${editValue.id}`;
      }
    } else {
      if (activeTab == "Type") {
        payload = {
          name: columnName.trim(),
          taxonomy_type: taxonomyType,
          brand_id: Number(brandType),
          description: description.trim() || undefined,
          ...(dialogMode === "add"
            ? { created_by: user.id }
            : { updated_by: user.id }),
          is_active: isActive,
          is_required: isMandatory,
          ...(bqColumnName
            ? {
              metadata_: {
                "BQ Column Name": bqColumnName,
              },
            }
            : { metadata_: {} }),
        };
        path =
          dialogMode == "add" ? `cdtype/add` : `cdtype/update/${editValue.id}`;
      } else {
        payload = {
          display_name: columnName.trim(),
          custom_domain_type_id: Number(taxonomyType),
          value: columnValue.trim(),
          ...(dialogMode === "add"
            ? { created_by: user.id }
            : { updated_by: user.id }),
          is_active: isActive,
        };
        path =
          dialogMode == "add"
            ? `cdvalue/add`
            : `cdvalue/update/${editValue.id}`;
      }
    }

    const entityLabel = `${mainTab} ${activeTab}`;
    const actionVerb =
      dialogMode === "add"
        ? `create_${mainTab === "Standard" ? "sd" : "cd"}${activeTab === "Type" ? "type" : "value"}`
        : `update_${mainTab === "Standard" ? "sd" : "cd"}${activeTab === "Type" ? "type" : "value"}`;

    try {
      const response = await apiClient({
        method: dialogMode === "add" ? "post" : "put",
        url: `txnmy/api/v1/${path}`,
        data: payload,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.message === "success") {
        let logMessage = "";
        let detailItems: { label: string; value: string }[] = [];
        let changeItems: Array<{ label: string; from?: string; to?: string }> =
          [];

        if (dialogMode === "add") {
          logMessage = buildSummary(entityLabel, columnName.trim(), "created");
          if (activeTab === "Value") {
            detailItems = buildDetails(
              {
                display_name: columnName.trim(),
                value: columnValue.trim(),
                type_name: taxonomyTypeName,
                brand: valueBrandId
                  ? formatIdWithName(valueBrandId, valueBrandName)
                  : "Not Set",
                is_active: isActive,
              },
              [
                { key: "display_name", label: "Display Name" },
                { key: "value", label: "Value" },
                { key: "type_name", label: "Type" },
                { key: "brand", label: "Brand" },
                {
                  key: "is_active",
                  label: "Status",
                  type: "boolean",
                  boolStyle: "activeInactive",
                },
              ] as FieldSpec[],
              { skipEmpty: true }
            );
          } else {
            const typeDetails: Record<string, any> = {
              name: columnName.trim(),
              taxonomy_type: taxonomyTypeName,
              bq_column_name: bqColumnName,
              description: description.trim(),
              is_active: isActive,
            };
            const typeSpecs: FieldSpec[] = [
              { key: "name", label: "Name" },
              { key: "taxonomy_type", label: "Taxonomy Type" },
              { key: "bq_column_name", label: "BQ Column Name" },
              { key: "description", label: "Description" },
              {
                key: "is_active",
                label: "Status",
                type: "boolean",
                boolStyle: "activeInactive",
              },
            ];
            if (mainTab === "Custom") {
              typeDetails.brand = formatIdWithName(brandType, brandName);
              typeDetails.is_mandatory = isMandatory;
              typeSpecs.push(
                { key: "brand", label: "Brand" },
                {
                  key: "is_mandatory",
                  label: "Mandatory",
                  type: "boolean",
                  boolStyle: "yesNo",
                }
              );
            }
            detailItems = buildDetails(typeDetails, typeSpecs, {
              skipEmpty: true,
            });
          }
        } else {
          const previous: Record<string, any> = {
            name: editValue?.name ?? "",
            value: editValue?.value ?? "",
            bq_column_name: editValue?.display_name ?? "",
            description: editValue?.description ?? "",
            is_active: editValue?.is_active,
            is_mandatory: editValue?.is_mandatory,
            brand_id: editValue?.brand_id,
          };
          const next: Record<string, any> = {
            name: columnName.trim(),
            value: columnValue.trim(),
            bq_column_name: bqColumnName,
            description: description.trim(),
            is_active: isActive,
            is_mandatory: isMandatory,
            brand_id: brandType ? Number(brandType) : editValue?.brand_id,
          };
          const updateSpecs: FieldSpec[] = [
            { key: "name", label: activeTab === "Value" ? "Display Name" : "Name" },
            {
              key: "is_active",
              label: "Status",
              type: "boolean",
              boolStyle: "activeInactive",
            },
          ];
          if (activeTab === "Type") {
            updateSpecs.push(
              { key: "bq_column_name", label: "BQ Column Name" },
              { key: "description", label: "Description" }
            );
          }
          if (activeTab === "Value") {
            updateSpecs.push({ key: "value", label: "Value" });
          }
          if (mainTab === "Custom" && activeTab === "Type") {
            updateSpecs.push(
              {
                key: "brand_id",
                label: "Brand",
                resolve: (id) => {
                  const b = brands.find((x: any) => x.id === Number(id));
                  return b?.name || String(id ?? "Not Set");
                },
              },
              {
                key: "is_mandatory",
                label: "Mandatory",
                type: "boolean",
                boolStyle: "yesNo",
              }
            );
          }
          changeItems = diffFields(previous, next, updateSpecs);
          logMessage = buildSummary(
            entityLabel,
            columnName.trim(),
            "updated",
            changeItems.length
          );
        }

        const desc = {
          message: logMessage,
          status: response.status,
          method: response.config?.method || (dialogMode === "add" ? "POST" : "PUT"),
          url: response.config?.url || `/txnmy/api/v1/${path}`,
          category: "taxonomy",
          action: actionVerb,
          ...(detailItems.length ? { details: detailItems } : {}),
          ...(changeItems.length ? { changes: changeItems } : {}),
        };
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);

        Toastify(
          "success",
          `${mainTab} ${activeTab} ${dialogMode == "add" ? "Created" : "Updated"} successfully.`
        );
        handleDialogClose();
        setRefresh((prev: boolean) => !prev);
      } else {
        throw new Error("Unexpected response format.");
      }
    } catch (error: any) {
      console.error(error);
      const detail = error.response?.data?.detail;
      const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail ?? '');
      const isDuplicate = /duplicate|unique constraint|unq_custom_domain_values/i.test(detailStr);
      const message = isDuplicate
        ? 'This value already exists for this type and brand.'
        : (typeof detail === 'string' ? detail : null) || `Failed to ${dialogMode == "add" ? "Create" : "Update"} ${mainTab} ${activeTab}`;
      Toastify('error', message);
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to ${dialogMode === "add" ? "create" : "update"} ${entityLabel} "${columnName.trim() || "N/A"}": ${message}`,
          status: error?.response?.status || 500,
          method: dialogMode === "add" ? "POST" : "PUT",
          url: `/txnmy/api/v1/${path}`,
          category: "taxonomy",
          action: actionVerb,
          error: message,
        }),
        true
      );
    } finally {
      setLoader(false);
    }
  };

  const handleDialogClose = () => {
    setOpenCreateDialog(false);
    setColumnName("");
    setTaxonomyType("");
    setDescription("");
    setIsMandatory(false);
    setIsActive(true);
    setValidationError(null);
    setColumnNameError(null);
    setBQColumnNameError(null);
    setDialogMode("add");
    setEditValue({});
    setColumnValue("");
    setBQColumnName("");
  };

  useEffect(() => {
    if (dialogMode == "edit" && editValue) {
      setColumnName(editValue.name);
      setTaxonomyType(editValue.type);
      setDescription(editValue.description);
      setIsActive(editValue.is_active);
      setActiveTab(editValue.active_tab);

      if (editValue.active_tab == "Value") {
        setColumnValue(editValue.value);
      } else {
        setBQColumnName(editValue.display_name);
      }

      if (mainTab == "Custom" && editValue.active_tab == "Type") {
        console.log(editValue);
        setBrandType(editValue.brand_id);
        setIsMandatory(editValue.is_mandatory);
      }
    } else if (dialogMode != "edit" && createModeValue) {
      setActiveTab(createModeValue.active_tab);
      if (createModeValue.active_tab == "Value") {
        setTaxonomyType(createModeValue.type_id);
      }
    }
  }, [editValue, createModeValue]);

  useEffect(() => {
    console.log(activeTab, dialogValue);
    if (activeTab == "Value") {
      const grouped = dialogValue.reduce((groups: any, type: any) => {
        // Grouping taxonomyTypes by 'taxonomy_type'
        if (!groups[type.taxonomy_type]) {
          groups[type.taxonomy_type] = [];
        }
        groups[type.taxonomy_type].push(type);
        return groups;
      }, {});
      setGroupedTypes(grouped);
    }
  }, [dialogValue, activeTab]);

  const isSubmitDisabled =
    loader ||
    !columnName.trim() ||
    !taxonomyType ||
    !!columnNameError ||
    !!bqColumnNameError ||
    (activeTab === "Value" && !columnValue.trim());

  return (
    <Sheet
      open={openCreateDialog}
      onOpenChange={(open) => {
        if (!open) {
          handleDialogClose();
        }
      }}
    >
      <SheetContent
        side="right"
        className="w-[500px] sm:max-w-[500px] flex flex-col h-full"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>
        {dialogMode == "add" ? "Create New" : "Edit"} {mainTab} {activeTab}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <div className="space-y-4 p-4">
            {/* Validation Error (client-side only; API errors shown via toast) */}
            {validationError && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{validationError}</span>
              </div>
            )}

            {/* Display Name */}
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
            value={columnName}
            onChange={(e) => {
              const value = e.target.value;
              setColumnName(value);
              if (activeTab === "Type") {
                validateColumnName(value, setColumnNameError);
              } else {
                setColumnNameError(null);
              }
            }}
                placeholder="Enter display name"
                className={cn(columnNameError && "border-destructive")}
              />
              {columnNameError && (
                <p className="text-sm text-destructive">{columnNameError}</p>
              )}
            </div>

            {/* BQ Column Name (Type only) */}
          {activeTab == "Type" && (
              <div className="space-y-2">
                <Label htmlFor="bqColumnName">BQ Column Name</Label>
                <Input
                  id="bqColumnName"
              value={bqColumnName}
              onChange={(e) => {
                setBQColumnName(e.target.value);
                if (e.target.value) {
                  validateColumnName(e.target.value, setBQColumnNameError);
                } else {
                  setBQColumnNameError(null);
                }
              }}
                  placeholder="Enter BQ column name"
                  className={cn(bqColumnNameError && "border-destructive")}
            />
                {bqColumnNameError && (
                  <p className="text-sm text-destructive">{bqColumnNameError}</p>
                )}
              </div>
          )}

            {/* Value (Value only) */}
          {activeTab == "Value" && (
              <div className="space-y-2">
                <Label htmlFor="columnValue">Value *</Label>
                <Input
                  id="columnValue"
              value={columnValue}
              onChange={(e) => setColumnValue(e.target.value)}
                  placeholder="Enter value"
                />
              </div>
            )}

            {/* Taxonomy/Domain Type */}
            <div className="space-y-2">
              <Label htmlFor="taxonomyType">
                {activeTab == "Type" ? "Taxonomy" : "Domain"} Type *
              </Label>
              <SingleSelect
                options={
                  activeTab == "Type"
                    ? taxonomyTypes.map((type) => ({
                        label: type.charAt(0).toUpperCase() + type.slice(1),
                        value: type,
                      }))
                    : Object.keys(groupedTypes).flatMap((taxonomyTypeKey) =>
                        groupedTypes[taxonomyTypeKey].map((type: any) => ({
                          label: `${taxonomyTypeKey.charAt(0).toUpperCase() + taxonomyTypeKey.slice(1)} — ${type.name}`,
                          value: type.id.toString(),
                        }))
                      )
                }
                defaultValue={taxonomyType?.toString() || ""}
                onChange={(value) => setTaxonomyType(value)}
                placeholder={`Select ${activeTab == "Type" ? "taxonomy" : "domain"} type`}
                disabled={dialogMode == "edit" && activeTab == "Type"}
                containerClassName="w-full"
              />
            </div>

            {/* Brand (Custom Type only) */}
            {mainTab == "Custom" && activeTab == "Type" && (
              <div className="space-y-2">
                <Label htmlFor="brand">Brand *</Label>
                <SingleSelect
                  options={brands.map((brand) => ({
                    label: brand.name,
                    value: brand.id.toString(),
                  }))}
                  defaultValue={brandType?.toString() || ""}
                  onChange={(value) => setBrandType(value)}
                  placeholder="Select brand"
                  containerClassName="w-full"
                />
              </div>
            )}

            {/* Description (Type only) */}
          {activeTab == "Type" && (
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description || ""}
              onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                  className="w-full resize-none"
              rows={3}
            />
              </div>
          )}

            {/* Status - label not bound to switch so only the switch control toggles */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium leading-none">Status</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {isActive ? "Active" : "Inactive"}
                </span>
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
            </div>

            {/* Is Mandatory (Custom Type only) */}
        {mainTab == "Custom" && activeTab == "Type" && (
              <div className="flex items-center space-x-2">
              <Checkbox
                  id="isMandatory"
                checked={isMandatory}
                  onCheckedChange={(checked) => setIsMandatory(checked as boolean)}
              />
                <Label htmlFor="isMandatory">Is Mandatory</Label>
              </div>
        )}
          </div>
        </div>

        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={handleDialogClose} disabled={loader}>
          Cancel
        </Button>
          <Button onClick={handleCreate} disabled={isSubmitDisabled}>
            {loader ? (
              <SpinnerLoader className="h-4 w-4" />
            ) : (
              `${dialogMode == "add" ? "Create" : "Update"} ${activeTab}`
            )}
        </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default CreateDialog;
